import { useState } from "react"
import { Link } from "react-router-dom"
import { GiftIcon } from "@heroicons/react/24/outline"
import { Button } from "@/components/Button"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useToast } from "@/components/Toast"
import { useAffiliates } from "@/hooks/useAffiliates"
import { useAttributeUserToAffiliate } from "@/hooks/useUserDetail"
import { APIError } from "@/api"

// Mounted only for a user with no attribution, so the affiliate list is not fetched otherwise.
function AttributeToAffiliate({ userId, userName }: { userId: string; userName: string }) {
  const { toast } = useToast()
  const { data, isLoading } = useAffiliates({
    status: "active",
    pageSize: 100,
    sortBy: "name",
    sortOrder: "asc",
  })
  const attribute = useAttributeUserToAffiliate()
  const [affiliatePublicId, setAffiliatePublicId] = useState("")
  const [confirming, setConfirming] = useState(false)

  const affiliates = data?.affiliates ?? []
  const chosen = affiliates.find((a) => a.public_id === affiliatePublicId)

  const onConfirm = async () => {
    try {
      const result = await attribute.mutateAsync({ userId, affiliatePublicId })
      toast({ title: "Attributed", description: `Credited to ${result.referring_affiliate.name}` })
    } catch (err) {
      toast({
        title: "Error attributing user",
        description: err instanceof APIError ? err.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-secondary">
        No affiliate is credited with this signup. If they came through a partner but the link
        couldn't follow them (Sign in with Apple, another device), attribute them here. It can't
        be changed afterward.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          aria-label="Affiliate"
          value={affiliatePublicId}
          onChange={(e) => setAffiliatePublicId(e.target.value)}
          disabled={isLoading || attribute.isPending}
          className="min-w-0 flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
        >
          <option value="">{isLoading ? "Loading affiliates..." : "Choose an affiliate"}</option>
          {affiliates.map((a) => (
            <option key={a.public_id} value={a.public_id}>
              {a.name} ({a.ref_code})
            </option>
          ))}
        </select>
        <Button
          onClick={() => setConfirming(true)}
          disabled={!affiliatePublicId || attribute.isPending}
        >
          {attribute.isPending ? "Saving..." : "Attribute"}
        </Button>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Attribute this user?"
        description={`${userName} will be credited to ${chosen?.name ?? "this affiliate"}, and payments from teams they create during that affiliate's program dates then count toward its commission under its terms. This can't be changed afterward.`}
        confirmLabel="Attribute"
        onConfirm={() => void onConfirm()}
      />
    </div>
  )
}

export function ReferringAffiliateCard({
  userId,
  userName,
  referringAffiliate,
}: {
  userId: string
  userName: string
  referringAffiliate: { public_id: string; name: string } | null
}) {
  return (
    <div className="bg-surface rounded-lg p-6">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
        <GiftIcon className="h-5 w-5 text-secondary" />
        Referring affiliate
      </h2>
      {referringAffiliate ? (
        <Link
          to={`/affiliates/${referringAffiliate.public_id}`}
          className="text-sm font-medium text-blue-600 hover:underline"
        >
          {referringAffiliate.name}
        </Link>
      ) : (
        <AttributeToAffiliate userId={userId} userName={userName} />
      )}
    </div>
  )
}
